import React, { useEffect, useRef } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import { useTerminalSocket } from '../hooks/useTerminalSocket';

const THEME = {
  background: '#0A0A0A',
  foreground: '#E5E5E5',
  cursor: '#00FF00',
  cursorAccent: '#0A0A0A',
  selectionBackground: '#00FF0040',
  black: '#0A0A0A',
  red: '#FF5555',
  green: '#00FF00',
  yellow: '#FFD700',
  blue: '#00BFFF',
  magenta: '#FF00FF',
  cyan: '#00FFFF',
  white: '#E5E5E5',
  brightBlack: '#4A4A4A',
  brightRed: '#FF6E6E',
  brightGreen: '#7FFF7F',
  brightYellow: '#FFE97F',
  brightBlue: '#7FD0FF',
  brightMagenta: '#FF7FFF',
  brightCyan: '#7FFFFF',
  brightWhite: '#FFFFFF',
};

const STATUS_LABEL = {
  connecting: { text: 'connecting…', color: '#FF9500' },
  connected: { text: 'connected', color: '#00FF00' },
  reconnecting: { text: 'reconnecting…', color: '#FF9500' },
  closed: { text: 'disconnected', color: '#888' },
  error: { text: 'error', color: '#FF0055' },
};

/**
 * Xterm.js terminal mounted into a fixed-height container, wired to the
 * backend bash PTY via `useTerminalSocket`. This component owns ONLY the
 * xterm.js instance + DOM/resize handling — all WS lifecycle, status, and
 * reconnect logic lives in the hook.
 */
const XtermTerminal = ({ height = 420, roomId = null }) => {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);

  const { status, errorMsg, sendInput, sendResize } = useTerminalSocket({
    roomId,
    getTerminal: () => termRef.current,
  });

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const term = new Terminal({
      theme: THEME,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: 13,
      cursorBlink: true,
      convertEol: true,
      scrollback: 2000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());
    term.open(containerRef.current);

    termRef.current = term;
    fitRef.current = fit;

    let isReady = false;
    const safeFit = () => {
      if (!isReady) return;
      try {
        const el = containerRef.current;
        if (!el || el.clientWidth <= 0 || el.clientHeight <= 0) return;
        // eslint-disable-next-line no-underscore-dangle
        const rs = term._core?._renderService;
        if (!rs?.dimensions?.css?.cell?.width) return;
        if (!rs?.dimensions?.css?.cell?.height) return;
        fit.fit();
      } catch (err) {
        console.debug('[xterm] fit skipped:', err?.message);
      }
    };

    const renderDisposer = term.onRender(() => {
      if (!isReady) {
        isReady = true;
        setTimeout(safeFit, 0);
      }
    });

    let ro;
    try {
      ro = new ResizeObserver(safeFit);
      ro.observe(containerRef.current);
    } catch (err) {
      console.debug('[xterm] ResizeObserver unavailable:', err?.message);
    }

    // Forward keystrokes + resize events to whichever WS is currently open.
    // The hook handles reconnects transparently behind these references.
    const onDataDisposer = term.onData((data) => sendInput(data));
    const onResizeDisposer = term.onResize(({ cols, rows }) => sendResize({ cols, rows }));

    const handleWindowResize = () => safeFit();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      if (ro) {
        try { ro.disconnect(); } catch (err) { console.debug('[xterm] RO disconnect:', err?.message); }
      }
      renderDisposer.dispose();
      window.removeEventListener('resize', handleWindowResize);
      onDataDisposer.dispose();
      onResizeDisposer.dispose();
      term.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusLabel = STATUS_LABEL[status];

  return (
    <div data-testid="xterm-terminal-wrapper">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800 bg-[#0F0F0F]">
        <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: statusLabel.color }} />
          <span style={{ color: statusLabel.color }} data-testid="xterm-status">
            {statusLabel.text}
          </span>
          {errorMsg && (
            <span className="ml-2 text-[#FF5555]" data-testid="xterm-error">
              · {errorMsg}
            </span>
          )}
        </div>
        <div className="text-[10px] text-gray-600 font-mono hidden sm:block">
          isolated sandbox · pro
        </div>
      </div>
      <div
        ref={containerRef}
        data-testid="xterm-terminal-container"
        style={{ height: `${height}px`, background: '#0A0A0A' }}
      />
    </div>
  );
};

export default XtermTerminal;
